import { formatDisplayDateTime } from "../../utils/dateUtils";
import React, { useEffect, useState, useRef } from "react";
import { AxiosError } from "axios";
import { Result, CustomGroup } from "../../interface/CustomIcd";
import { CustomIcdAxiosInstance } from "../../Network/CustomIcd";
import { Option } from "../../interface/Option";
import { ColumnDef } from "@tanstack/react-table";
import { Button } from "../../components/ui/button";
import ListofMedicalGroup from "../Shared/ListofMedicalGroup";
import ListofCustom from "Pages/Shared/ListofcustemIcd";
import { LoginAxiosInstance } from "../../Network/LoginAxiosInstance";
import Listofmedicalcode from "../Shared/ListofIcdmedicalcode";
import { Eye } from "lucide-react";
import { X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/Label";
import { Toaster } from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import DataTable2 from "shared/DataTable2";
import UnifiedSearchBox from "../../shared/UnifiedSearchBox";
import ServerDeletedPills from "shared/ServerDeletedPills";
import { ServerDeletedFilterValue } from "shared/ServerListFilters";
import { useServerPaginatedList } from "shared/useServerPaginatedList";
import FileUpload from "../uploadFile/UploadFileCustomGroup/UploadFileCustomGroup";

// A linked group's status is independent of the parent group's status.
// Missing status is unknown, not implicitly inactive.
const isLinkedGroupInactive = (group: any): boolean => {
  if (!group || typeof group !== "object") return false;
  if (group.__inactive === true) return true;
  const value = group.is_active ?? group.Is_active ?? group.isActive ?? group.status ?? group.Status;
  return value === false || value === 0 || value === "0" ||
    (typeof value === "string" && ["inactive", "disabled", "false"].includes(value.trim().toLowerCase()));
};

// Keep the original SQL ID and status for existing linked selections.
const linkedGroupOption = (group: any): Option => {
  const source = typeof group === "object" && group !== null ? group : { id: group, name: String(group) };
  const id = source.id ?? source.dbId ?? source.value;
  return {
    ...source,
    value: id,
    label: String(source.name ?? source.label ?? id ?? ""),
    __inactive: isLinkedGroupInactive(source),
  } as Option;
};

// ─── Role Helper ──────────────────────────────────────────────────────────────
const getUserRole = (): string => {
  try {
    const token = localStorage.getItem("token") || "";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.userType || "";
  } catch { return ""; }
};


// ─── QA Automation IDs ───────────────────────────────────────────────────────
// Stable selectors for Playwright / Cypress / Selenium.
// Additive only: no API, payload, validation logic, permissions, routing,
// visual style, element order, or UI behavior changes.
const QA_PAGE_PREFIX = "custom-icd-group";

const qaSafe = (value: string | number | undefined | null) =>
  String(value ?? "unknown")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const qaId = (suffix: string | number) => `${QA_PAGE_PREFIX}-${qaSafe(suffix)}`;

const qaAttrs = (suffix: string | number) => ({
  id: qaId(suffix),
  "data-testid": qaId(suffix),
  "data-qa": qaId(suffix),
});

const rowQaId = (action: string, id: string | number | undefined) =>
  qaId(`${action}-${id ?? "unknown"}`);

// ─── Status Dropdown ──────────────────────────────────────────────────────────
const StatusDropdown: React.FC<{ value: string; onChange: (v: string) => void; qaIdPrefix?: string }> = ({ value, onChange, qaIdPrefix }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const options = [
    { value: "",      label: "All Status" },
    { value: "true",  label: "Active"     },
    { value: "false", label: "Deactivate" },
  ];
  const selected = options.find((o) => o.value === value) || options[0];

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const statusQaId = qaIdPrefix ?? qaId("status-dropdown");

  return (
    <div
      ref={ref}
      id={statusQaId}
      data-testid={statusQaId}
      data-qa={statusQaId}
      style={{ position: "relative", userSelect: "none", minWidth: 150 }}
    >
      <button
        id={`${statusQaId}-trigger`}
        data-testid={`${statusQaId}-trigger`}
        data-qa={`${statusQaId}-trigger`}
        data-qa-open={open ? "true" : "false"}
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%", height: 38, padding: "0 12px",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
          background: "#fff", border: `1.5px solid ${open ? "#1e3a8a" : "#cbd5e1"}`,
          borderRadius: 8, fontSize: 14, color: "#1e293b",
          cursor: "pointer", transition: "border-color 0.15s", boxSizing: "border-box" as const,
        }}
      >
        <span
          id={`${statusQaId}-selected-label`}
          data-testid={`${statusQaId}-selected-label`}
          data-qa={`${statusQaId}-selected-label`}
          style={{ color: value ? "#1e293b" : "#94a3b8", fontWeight: value ? 500 : 400 }}
        >
          {selected.label}
        </span>
        <svg
          id={`${statusQaId}-icon`}
          data-testid={`${statusQaId}-icon`}
          data-qa={`${statusQaId}-icon`}
          width="13" height="13" viewBox="0 0 24 24" fill="none"
          stroke={open ? "#1e3a8a" : "#94a3b8"} strokeWidth="2.5" strokeLinecap="round"
          style={{ flexShrink: 0, transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "rotate(0deg)" }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div
          id={`${statusQaId}-menu`}
          data-testid={`${statusQaId}-menu`}
          data-qa={`${statusQaId}-menu`}
          style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, width: "100%",
          background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 8,
          boxShadow: "0 8px 24px rgba(0,0,0,0.09)", zIndex: 9999, overflow: "hidden",
        }}>
          {options.map((opt) => {
            const isSel = opt.value === value;
            return (
              <div
                key={opt.value}
                id={`${statusQaId}-option-${qaSafe(opt.value || "all")}`}
                data-testid={`${statusQaId}-option-${qaSafe(opt.value || "all")}`}
                data-qa={`${statusQaId}-option-${qaSafe(opt.value || "all")}`}
                data-qa-selected={isSel ? "true" : "false"}
                onClick={() => { onChange(opt.value); setOpen(false); }}
                style={{
                  padding: "10px 12px", fontSize: 14, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  background: isSel ? "#eff6ff" : "transparent",
                  color: isSel ? "#1e3a8a" : "#374151",
                  fontWeight: isSel ? 600 : 400,
                  borderBottom: "1px solid #f1f5f9",
                }}
                onMouseEnter={(e) => { if (!isSel) (e.currentTarget as HTMLDivElement).style.background = "#f8fafc"; }}
                onMouseLeave={(e) => { if (!isSel) (e.currentTarget as HTMLDivElement).style.background = "transparent"; }}
              >
                <span>{opt.label}</span>
                {isSel && (
                  <svg
          id={`${statusQaId}-icon`}
          data-testid={`${statusQaId}-icon`}
          data-qa={`${statusQaId}-icon`}
          width="13" height="13" viewBox="0 0 24 24" fill="none"
                    stroke="#1e3a8a" strokeWidth="2.5" strokeLinecap="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
// ─── SectionCard ──────────────────────────────────────────────────────────────
const SectionCard: React.FC<{ num: number; title: string; subtitle?: string; automationId?: string; children: React.ReactNode }> = ({ num, title, subtitle, automationId, children }) => (
  <div
    {...(automationId ? { id: automationId, "data-testid": automationId, "data-qa": automationId } : {})}
    className="border border-gray-200 rounded-xl overflow-hidden mb-4 bg-white shadow-sm"
  >
    <div className="flex items-center gap-3 px-5 py-3 bg-gray-50 border-b border-gray-200">
      <span
        {...(automationId ? { id: `${automationId}-number`, "data-testid": `${automationId}-number`, "data-qa": `${automationId}-number` } : {})}
        className="w-6 h-6 rounded-full bg-blue-700 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0"
      >
        {num}
      </span>
      <div>
        <p
          {...(automationId ? { id: `${automationId}-title`, "data-testid": `${automationId}-title`, "data-qa": `${automationId}-title` } : {})}
          className="text-sm font-semibold text-gray-800"
        >
          {title}
        </p>
        {subtitle && (
          <p
            {...(automationId ? { id: `${automationId}-subtitle`, "data-testid": `${automationId}-subtitle`, "data-qa": `${automationId}-subtitle` } : {})}
            className="text-xs text-gray-500"
          >
            {subtitle}
          </p>
        )}
      </div>
    </div>
    <div
      {...(automationId ? { id: `${automationId}-body`, "data-testid": `${automationId}-body`, "data-qa": `${automationId}-body` } : {})}
      className="p-5"
    >
      {children}
    </div>
  </div>
);

// ─── StatusToggle ─────────────────────────────────────────────────────────────
const StatusToggle: React.FC<{ label: string; dotColor: string; value: boolean; onChange: (v: boolean) => void; qaIdPrefix?: string }> = ({ label, dotColor, value, onChange, qaIdPrefix }) => {
  const statusToggleQaId = qaIdPrefix ?? qaId(`status-toggle-${label}`);
  return (
  <div
    id={statusToggleQaId}
    data-testid={statusToggleQaId}
    data-qa={statusToggleQaId}
    data-qa-value={value ? "active" : "inactive"}
    className="border border-gray-200 rounded-lg p-3 bg-gray-50"
  >
    <div className="flex items-center gap-2 mb-2">
      <span
        id={`${statusToggleQaId}-dot`}
        data-testid={`${statusToggleQaId}-dot`}
        data-qa={`${statusToggleQaId}-dot`}
        className="w-2 h-2 rounded-full flex-shrink-0"
        style={{ background: dotColor }}
      />
      <span
        id={`${statusToggleQaId}-label`}
        data-testid={`${statusToggleQaId}-label`}
        data-qa={`${statusToggleQaId}-label`}
        className="text-xs font-medium text-gray-600"
      >
        {label}
      </span>
    </div>
    <div className="flex gap-2">
      <button
        id={`${statusToggleQaId}-active`}
        data-testid={`${statusToggleQaId}-active`}
        data-qa={`${statusToggleQaId}-active`}
        data-qa-selected={value ? "true" : "false"}
        type="button"
        onClick={() => onChange(true)}
        className={`flex-1 py-1.5 rounded-md text-xs font-medium border transition-all ${value ? "bg-green-50 text-green-800 border-green-300" : "bg-white text-gray-400 border-gray-200 hover:bg-gray-100"}`}>
        Active
      </button>
      <button
        id={`${statusToggleQaId}-inactive`}
        data-testid={`${statusToggleQaId}-inactive`}
        data-qa={`${statusToggleQaId}-inactive`}
        data-qa-selected={!value ? "true" : "false"}
        type="button"
        onClick={() => onChange(false)}
        className={`flex-1 py-1.5 rounded-md text-xs font-medium border transition-all ${!value ? "bg-red-50 text-red-800 border-red-300" : "bg-white text-gray-400 border-gray-200 hover:bg-gray-100"}`}>
        Inactive
      </button>
    </div>
  </div>
  );
};

// ─── ICD Search Input ─────────────────────────────────────────────────────────
const IcdSearchInput: React.FC<{ value: string; onChange: (v: string) => void; placeholder?: string; qaIdPrefix?: string }> = ({ value, onChange, placeholder = "Search ICD code...", qaIdPrefix }) => {
  const searchQaId = qaIdPrefix ?? qaId("icd-search-input");
  return (
    <div
      id={searchQaId}
      data-testid={searchQaId}
      data-qa={searchQaId}
      style={{ position: "relative", minWidth: 200 }}
    >
      <div style={{
        position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
        pointerEvents: "none", display: "flex", alignItems: "center",
      }}>
        <svg
          id={`${searchQaId}-icon`}
          data-testid={`${searchQaId}-icon`}
          data-qa={`${searchQaId}-icon`}
          width="13" height="13" viewBox="0 0 24 24" fill="none"
          stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round">
          <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
      </div>
      <input
        id={`${searchQaId}-input`}
        data-testid={`${searchQaId}-input`}
        data-qa={`${searchQaId}-input`}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          height: 38, width: "100%", padding: "0 30px 0 30px",
          border: `1.5px solid ${value ? "#1e3a8a" : "#cbd5e1"}`,
          borderRadius: 8, fontSize: 14, color: "#1e293b",
          outline: "none", transition: "border-color 0.15s", boxSizing: "border-box" as const,
          background: value ? "#eff6ff" : "#fff",
        }}
      />
      {value && (
        <button
          id={`${searchQaId}-clear`}
          data-testid={`${searchQaId}-clear`}
          data-qa={`${searchQaId}-clear`}
          type="button"
          onClick={() => onChange("")}
          style={{
            position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
            background: "#e2e8f0", border: "none", borderRadius: "50%",
            width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", fontSize: 11, color: "#64748b", padding: 0,
          }}
        >
          ×
        </button>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const CustomIcdGroups: React.FC = () => {
  const [items, setItems] = useState<CustomGroup>({ result: [] });

  // ── Role-based access ────────────────────────────────────────────────────
  const userRole = getUserRole();
  const canManageDeleted = userRole === "system_admin" || userRole === "super_super_admin";

  // ── Force Dialog state ───────────────────────────────────────────────────
  const [forceDialogOpen, setForceDialogOpen] = useState(false);
const [forceAction, setForceAction] = useState<"softDelete" | "hardDelete" | "status" | null>(null);
  const [forceId, setForceId] = useState<string | number | null>(null);
  const [forceError, setForceError] = useState<string>("");

  const [Name, setName] = useState<string>("");
  const [IcdDescription, setIcdDescription] = useState<string>("");
  const [medicalcode, setMedicalcode] = useState<Option[]>([]);
  const [selectedIcds, setSelectedIcds] = useState<string[]>([]);
  const icdDialogRef = useRef<HTMLDialogElement | null>(null);
  const [Description, setDescription] = useState<string>("");
  const [medicalIcdGroups, setMedicalIcdGroups] = useState<Option[]>([]);
  const [CustomIcdGroups, setCustomIcdGroups] = useState<Option[]>([]);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [editIndex, setEditIndex] = useState<null | number>(null);
  const [clearmedicalIcdGroups, setClearMedicalIcdGroups] = useState(false);
  const [clearcustomIcdGroups, setClearCustomIcdGroups] = useState(false);
  const [clearmedicalcode, setClearMedicalcode] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Result | null>(null);
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const token = localStorage.getItem("token") || "";
  const { toast } = useToast();
  const [usersMap, setUsersMap] = useState<Record<number, string>>({});
  const [allCodesOpen, setAllCodesOpen] = useState(false);
  const [allCodesItems, setAllCodesItems] = useState<Array<{ label: string; type: string; inactive?: boolean }>>([]);
  const [allCodesTitle, setAllCodesTitle] = useState("");
  const [forceTargetItem, setForceTargetItem] = useState<any>(null);
  const [showAllUpdates, setShowAllUpdates] = useState(false);

  // ─── Filter States ────────────────────────────────────────────────────────
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [deletedFilter, setDeletedFilter] = useState<ServerDeletedFilterValue>("false");
  const [filterSearch, setFilterSearch] = useState<string>("");

  // ─── QA Automation: nested shared dropdowns/search ─────────────────────────
  // Some controls are rendered inside shared components / react-select.
  // This attaches stable selectors to the actual inner input/select element.
  const applyNestedControlQaAttrs = () => {
    const setControlQaAttrs = (wrapperQaId: string, controlTestId: string) => {
      const wrapper = document.getElementById(wrapperQaId);
      const control = wrapper?.querySelector("input, select, textarea") as
        | HTMLInputElement
        | HTMLSelectElement
        | HTMLTextAreaElement
        | null;

      if (control) {
        control.setAttribute("id", controlTestId);
        control.setAttribute("data-testid", controlTestId);
        control.setAttribute("data-qa", controlTestId);
      }
    };

    setControlQaAttrs(qaId("wrapper-toolbar-search"), "custom-icd-group-input-search");
    setControlQaAttrs(qaId("wrapper-list-of-icd"), "custom-icd-group-select-icd");
    setControlQaAttrs(qaId("wrapper-medical-icd-groups"), "custom-icd-group-select-medical-icd-group");
    setControlQaAttrs(qaId("wrapper-custom-icd-groups"), "custom-icd-group-select-custom-icd-group");
  };

  useEffect(() => {
    const timer = window.setTimeout(applyNestedControlQaAttrs, 0);
    return () => window.clearTimeout(timer);
  }, [
    isDialogOpen,
    filterSearch,
    clearmedicalcode,
    clearmedicalIcdGroups,
    clearcustomIcdGroups,
    medicalcode.length,
    medicalIcdGroups.length,
    CustomIcdGroups.length,
  ]);


  const labelStyle: React.CSSProperties = {
    fontSize: 11, fontWeight: 500, color: "#9ca3af",
    textTransform: "capitalize", letterSpacing: "0.6px", marginBottom: 6,
  };
  const valStyle: React.CSSProperties = {
    margin: 0, fontSize: 14, fontWeight: 500, color: "#111827",
  };
  const dividerStyle: React.CSSProperties = {
    height: "0.5px", background: "#e5e7eb",
  };

  // ─── Server-side pagination and filters ───────────────────────────────────
  // The same unified search value is sent as both `name` and `disease`:
  // - `name` searches the Custom ICD Group name.
  // - `disease` searches ICD codes and the linked Medical/Custom Group names.
  const listFilters = {
    search: filterSearch.trim(),
    is_active: filterStatus,
    is_deleted: deletedFilter,
  };

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalCount,
    allowedPageSizes,
    isTableLoading,
    manualPagination,
    fetchItems,
  } = useServerPaginatedList<Result, typeof listFilters>({
    filters: listFilters,
    fetchPage: ({ page, limit, filters }) => {
      const search = filters.search || undefined;

      return CustomIcdAxiosInstance.get("getCustomGroup", {
        params: {
          page,
          limit,
          name: search,
          disease: search,
          is_active: filters.is_active || undefined,
          is_deleted: filters.is_deleted,
        },
      });
    },
    setRows: (rows) => setItems({ result: rows }),
    // Preserve the exact order returned by the backend.
    // The UI must not renumber/reorder records based on the visible row position.
    sortRows: (rows) => rows,
    onError: (error) => {
      console.error("Error fetching Custom ICD Groups:", error);
      toast({
        title: "Error",
        description: "Failed to fetch Custom ICD Groups",
        variant: "destructive",
      });
    },
  });

  const filteredData = items.result;

  useEffect(() => { fetchUsers(); }, []);

  // ── Soft Delete ──────────────────────────────────────────────────────────
  const removeItem = async (id: string | number): Promise<void> => {
    try {
      const response = await CustomIcdAxiosInstance.delete(`softDeleteCustomGroup/${id}`, { data: { token, force: false } });
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "Custom ICD group Deleted Successfully." });
      } else {
        setForceId(id); setForceAction("softDelete");
        setForceError(response.data.error || "This record is used elsewhere.");
        setForceDialogOpen(true);
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.error || "Failed to delete";
      if (error.response?.status === 409 || errMsg.toLowerCase().includes("used")) {
        setForceId(id); setForceAction("softDelete"); setForceError(errMsg); setForceDialogOpen(true);
      } else {
        toast({ title: "Error", description: errMsg, variant: "destructive" });
      }
    }
  };

  // ── Restore ──────────────────────────────────────────────────────────────
  const restoreItem = async (id: string | number): Promise<void> => {
    try {
      const response = await CustomIcdAxiosInstance.patch(`restoreCustomGroup/${id}`, { token });
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "Custom ICD group restored successfully." });
      } else {
        toast({ title: "Error", description: response.data.error || "Failed to restore", variant: "destructive" });
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.response?.data?.error || "Failed to restore", variant: "destructive" });
    }
  };

  // ── Hard Delete ──────────────────────────────────────────────────────────
  const permanentDeleteItem = async (id: string | number, force = false): Promise<void> => {
    try {
      const response = await CustomIcdAxiosInstance.delete(`deleteCustomGroup/${id}`, {
        data: { token, force },
      });
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: "Custom ICD group Permanently Deleted." });
      } else {
        setForceAction("hardDelete");
        setForceError(response.data.error || "This record is used elsewhere.");
      }
    } catch (error: any) {
      setForceAction("hardDelete");
      setForceError(error.response?.data?.error || "Failed to delete");
    }
  };

  // ── Force Soft Delete ────────────────────────────────────────────────────
  const forceSoftDelete = async (id: string | number): Promise<void> => {
    try {
      const response = await CustomIcdAxiosInstance.delete(`softDeleteCustomGroup/${id}`, { data: { token, force: true } });
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: "Custom ICD group force Deleted Successfully." });
      } else {
        setForceError(response.data.error || "Failed to force delete");
      }
    } catch (error: any) {
      setForceError(error.response?.data?.error || "Failed to force delete");
    }
  };

  // Always fetch the latest backend record by its real database ID before View/Edit.
  const fetchCustomGroupById = async (id: string | number): Promise<Result> => {
    const response = await CustomIcdAxiosInstance.get(`getCustomGroup/${id}`);
    const payload =
      response.data?.result ??
      response.data?.getCustomGroup ??
      response.data?.getcustemGroups ??
      response.data;

    if (!payload || response.data?.success === false) {
      throw new Error(response.data?.error || "Custom ICD group not found.");
    }

    return payload as Result;
  };

  const handleOpenDialog = async (item: Result) => {
    try {
      const freshItem = await fetchCustomGroupById(item.id);
      setSelectedItem(freshItem);
      dialogRef.current?.showModal();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.error || error.message || "Failed to load Custom ICD group details.",
        variant: "destructive",
      });
    }
  };

const handleCloseDialog = () => { setSelectedItem(null); setShowAllUpdates(false); };
  const handleOpenIcdDialog  = (icds: string[]) => { setSelectedIcds(icds); icdDialogRef.current?.showModal(); };
  const handleCloseIcdDialog = () => { icdDialogRef.current?.close(); setSelectedIcds([]); };

  const handleEditClick = async (item: Result) => {
    try {
      const freshItem = await fetchCustomGroupById(item.id);
      setClearMedicalIcdGroups(false); setClearCustomIcdGroups(false); setClearMedicalcode(false);
      handleEdit(freshItem);
      setIsDialogOpen(true);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.error || error.message || "Failed to load Custom ICD group for editing.",
        variant: "destructive",
      });
    }
  };

  const handleAddClick  = () => { setEditIndex(null); clear(); setIsDialogOpen(true); };

  const handlemedicalicdChange  = (selectedOptions: Option[]) => setMedicalIcdGroups(selectedOptions);
  const handlecustomicdChange   = (selectedOptions: Option[]) => setCustomIcdGroups(selectedOptions);
  const handlemedicalcodeChange = (selectedOptions: Option[]) => setMedicalcode(selectedOptions);

  // Backend relations are database IDs, not display names.
  const toRelationIds = (options: Option[]): number[] =>
    options
      .map((option) => Number(option.value))
      .filter((id) => Number.isFinite(id) && id > 0);

  const clear = () => {
    setName(""); setMedicalcode([]); setDescription("");
    setMedicalIcdGroups([]); setCustomIcdGroups([]);
    setClearMedicalIcdGroups(true); setClearCustomIcdGroups(true); setIsActive(true);
  };

  const fetchUsers = async () => {
    try {
      const res = await LoginAxiosInstance.get("/");
      const raw: any[] = res.data?.users ?? res.data?.user ?? res.data ?? [];
      const map: Record<number, string> = {};
      raw.forEach((u: any) => {
        if (u.id != null) map[Number(u.id)] = u.name ?? u.username ?? String(u.id);
      });
      setUsersMap(map);
    } catch {}
  };

  const resolveUserName = (val: any): string => {
    if (val == null) return "—";
    if (typeof val === "object") return val.name ?? val.username ?? "—";
    if (typeof val === "string" && isNaN(Number(val))) return val;
    return usersMap[Number(val)] ?? String(val);
  };

const toggleActiveStatus = async (item: Result, force = false): Promise<void> => {
  try {
    const ValuesMedical = (item.medical_groups || [])
      .map((g: any) => Number(g?.id ?? g))
      .filter((id: number) => Number.isFinite(id) && id > 0);
    const ValuesCustom = (item.custom_groups || [])
      .map((g: any) => Number(g?.id ?? g))
      .filter((id: number) => Number.isFinite(id) && id > 0);
    const response = await CustomIcdAxiosInstance.put("updateCustomGroup", {
      data: { name: item.name, icd: item.icd || [], description: item.description,
        medical_groups: ValuesMedical, custom_groups: ValuesCustom,
        is_active: !item.is_active, id: item.id },
      token,
      force,
    });
    if (response.data.success) {
      setItems((prev) => ({
        result: prev.result.map((r) => r.id === item.id ? { ...r, is_active: !item.is_active } : r),
      }));
      setForceDialogOpen(false); setForceId(null); setForceTargetItem(null); setForceError(""); setForceAction(null);
      toast({ title: "Success", description: `Status changed to ${!item.is_active ? "Active" : "Inactive"}.` });
    } else {
      setForceId(item.id);
      setForceTargetItem(item);
      setForceAction("status");
      setForceError(response.data.error || "This record is used elsewhere.");
      setForceDialogOpen(true);
    }
  } catch (error: any) {
    const errMsg = error.response?.data?.error || "";
    if (error.response?.status === 409 || errMsg.toLowerCase().includes("used") || errMsg.toLowerCase().includes("referenced") || errMsg.toLowerCase().includes("cannot")) {
      setForceId(item.id); setForceTargetItem(item); setForceAction("status");
      setForceError(errMsg || "This record is used elsewhere."); setForceDialogOpen(true);
    } else {
      toast({ title: "Error", description: errMsg || "An unexpected error occurred.", variant: "destructive" });
    }
  }
};

  const addItem = async (): Promise<void> => {
    if (!Name.trim()) { toast({ title: "Error", description: "Name is required to add this group.", variant: "destructive" }); return; }
    if (Name.trim().length < 3) { toast({ title: "Error", description: "Name must be at least 3 characters long.", variant: "destructive" }); return; }
    if (!medicalcode.length || medicalcode.every((option) => !option.value)) { toast({ title: "Error", description: "At least one active ICD code is required to add this group.", variant: "destructive" }); return; }
    try {
      const payload = {
        data: [{
          name: Name, icd: medicalcode.map((o) => o.value), description: Description,
          medical_groups: toRelationIds(medicalIcdGroups),
          custom_groups: toRelationIds(CustomIcdGroups), is_active: isActive,
        }],
        token,
      };
      const response = await CustomIcdAxiosInstance.post("addNewCustomGroup", payload);
      if (response.data.success) {
        await fetchItems();
        setCurrentPage(0);
        clear(); setIsDialogOpen(false);
        toast({ title: "Success", description: "New Custom ICD Group Added Successfully." });
      } else {
        const err = response.data.errors?.[0];
        toast({ title: "Error", description: err?.error || response.data.error || "Failed to add the group.", variant: "destructive" });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  const handleEdit = (item: Result) => {
    setName(item.name);
    setMedicalcode(item.icd.map((code) => ({ value: code, label: code })));
    const MedicalIcdGroupsData = Array.isArray(item.medical_groups)
      ? item.medical_groups.map((obj) => typeof obj === "string" ? { id: 0, name: obj } : obj) : [];
    setMedicalIcdGroups(MedicalIcdGroupsData.map(linkedGroupOption));
    const CustomIcdGroupsData = Array.isArray(item.custom_groups)
      ? item.custom_groups.map((obj) => typeof obj === "string" ? { id: 0, name: obj } : obj) : [];
    setCustomIcdGroups(CustomIcdGroupsData.map(linkedGroupOption));
    setDescription(item.description);
    setIsActive(item.is_active);
    setEditIndex(item.id);
  };

  const updateItem = async (id: string | number): Promise<void> => {
    if (!Name.trim()) { toast({ title: "Error", description: "Name is required to add this group.", variant: "destructive" }); return; }
    if (Name.trim().length < 3) { toast({ title: "Error", description: "Name must be at least 3 characters long.", variant: "destructive" }); return; }
    if (!medicalcode.length || medicalcode.every((option) => !option.value)) { toast({ title: "Error", description: "At least one active ICD code is required to add this group.", variant: "destructive" }); return; }
    try {
      const updatedItem = {
        data: {
          name: Name, icd: medicalcode.map((o) => o.value), description: Description,
          medical_groups: toRelationIds(medicalIcdGroups),
          custom_groups: toRelationIds(CustomIcdGroups), is_active: isActive, id,
        },
        token,
        force: false,
      };
      const response = await CustomIcdAxiosInstance.put(`updateCustomGroup`, updatedItem);
      if (response.data.success) {
        await fetchItems();
        clear(); setIsDialogOpen(false);
        toast({ title: "Success", description: "Custom ICD Group updated successfully." });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  const handleDownload = (item: Result) => {
    const rows = ["name;description;icd;medicalGroups;customGroups;is_active"];
    const icdCodes     = item.icd || [];
    const medicalGroups = item.medical_groups || [];
    const customGroups  = item.custom_groups  || [];
    const maxLen = Math.max(icdCodes.length, medicalGroups.length, customGroups.length);
    if (maxLen > 0) {
      rows.push(`${item.name || ""};${item.description || ""};${icdCodes[0] || ""};${medicalGroups[0]?.name || ""};${customGroups[0]?.name || ""};${item.is_active ? "TRUE" : "FALSE"}`);
      for (let i = 1; i < maxLen; i++) {
        rows.push(`${item.name || ""};;${icdCodes[i] || ""};${medicalGroups[i]?.name || ""};${customGroups[i]?.name || ""};`);
      }
    } else {
      rows.push(`${item.name || ""};${item.description || ""};;;;${item.is_active ? "TRUE" : "FALSE"}`);
    }
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `custom_icd_group_${item.id}.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return "—";
    return formatDisplayDateTime(dateStr);
  };

  const columns: ColumnDef<Result>[] = [
    {
      accessorKey: "id",
      header: ({ column }) => (
        <Button {...qaAttrs("sort-id")} variant="ghost" className="w-full justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          ID{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
     cell: ({ row }) => (
  <div
    id={rowQaId("cell-id", row.original.id)}
    data-testid={rowQaId("cell-id", row.original.id)}
    data-qa={rowQaId("cell-id", row.original.id)}
    className="w-full flex justify-center font-medium"
  >
    {currentPage * pageSize + row.index + 1}
  </div>
),
    },
    {
      accessorKey: "name",
      header: ({ column }) => (
        <Button {...qaAttrs("sort-name")} variant="ghost" className="w-full justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Name{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => (
        <div
          id={rowQaId("cell-name", row.original.id)}
          data-testid={rowQaId("cell-name", row.original.id)}
          data-qa={rowQaId("cell-name", row.original.id)}
          className={`w-full text-center ${(row.original as any).is_deleted ? "line-through text-gray-400" : ""}`}
        >
          {row.getValue("name")}
        </div>
      ),
    },
    {
      accessorKey: "description",
      header: ({ column }) => (
        <Button {...qaAttrs("sort-description")} variant="ghost" className="w-full justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Description{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => (
        <div
          id={rowQaId("cell-description", row.original.id)}
          data-testid={rowQaId("cell-description", row.original.id)}
          data-qa={rowQaId("cell-description", row.original.id)}
          className="w-full text-center"
        >
          {row.getValue("description")}
        </div>
      ),
    },
    {
      accessorKey: "icd",
      header: ({ column }) => (
        <Button {...qaAttrs("sort-icd")} variant="ghost" className="w-full justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          ICD{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        const icds = Array.isArray(item.icd) ? item.icd : [];
        const customGroups = Array.isArray(item.custom_groups)
          ? item.custom_groups.map((g: any) => ({ label: String(g?.name ?? g?.label ?? g), type: "Custom", inactive: isLinkedGroupInactive(g) })) : [];
        const medicalGroups = Array.isArray(item.medical_groups)
          ? item.medical_groups.map((g: any) => ({ label: String(g?.name ?? g?.label ?? g), type: "Medical", inactive: isLinkedGroupInactive(g) })) : [];
        const allValues = [
          ...icds.map((c: string) => ({ label: c, type: "ICD", inactive: false })),
          ...medicalGroups,
          ...customGroups,
        ];
        if (allValues.length === 0) {
          return (
            <div
              id={rowQaId("cell-icd-empty", item.id)}
              data-testid={rowQaId("cell-icd-empty", item.id)}
              data-qa={rowQaId("cell-icd-empty", item.id)}
              className="w-full text-center text-gray-400"
            >
              -
            </div>
          );
        }
        const searchTerm = filterSearch.toLowerCase().trim();
        const hasSearch = searchTerm.length >= 2;
        const matchingValues = hasSearch ? allValues.filter((c) => c.label.toLowerCase().includes(searchTerm)) : [];
        const displayedValues = hasSearch && matchingValues.length > 0 ? matchingValues.slice(0, 3) : allValues.slice(0, 3);
        const remainingCount = hasSearch && matchingValues.length > 0 ? matchingValues.length - 3 : allValues.length - 3;
        const getStyle = (type: string, isMatching: boolean) => {
          if (isMatching) return { bg: "#fef08a", color: "#713f12", border: "#facc15", tag: type === "ICD" ? "ICD" : type === "Medical" ? "MED" : "CUS" };
          if (type === "ICD") return { bg: "#011934", color: "#fff", border: "#011934", tag: "ICD" };
          if (type === "Medical") return { bg: "#f0fdf4", color: "#15803d", border: "#86efac", tag: "MED" };
          return { bg: "#faf5ff", color: "#7e22ce", border: "#d8b4fe", tag: "CUS" };
        };
        return (
          <div
            id={rowQaId("cell-icd", item.id)}
            data-testid={rowQaId("cell-icd", item.id)}
            data-qa={rowQaId("cell-icd", item.id)}
            className="w-full px-2 py-1.5"
          >
            <div
              id={rowQaId("icd-chip-list", item.id)}
              data-testid={rowQaId("icd-chip-list", item.id)}
              data-qa={rowQaId("icd-chip-list", item.id)}
              className="flex flex-wrap justify-center gap-1.5 mb-1"
            >
              {displayedValues.map((c, idx) => {
                const isMatching = hasSearch && c.label.toLowerCase().includes(searchTerm);
                const style = c.inactive ? { bg: "#e2e8f0", color: "#64748b", border: "#cbd5e1", tag: c.type === "Medical" ? "MED" : "CUS" } : getStyle(c.type, isMatching);
                return (
                  <div
                    key={idx}
                    id={rowQaId(`icd-chip-${idx}`, item.id)}
                    data-testid={rowQaId(`icd-chip-${idx}`, item.id)}
                    data-qa={rowQaId(`icd-chip-${idx}`, item.id)}
                    data-qa-label={c.label}
                    data-qa-type={c.type}
                    style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 4px 2px 8px", borderRadius: 6, background: style.bg, border: `1px solid ${style.border}` }}
                  >
                    <span id={rowQaId(`icd-chip-label-${idx}`, item.id)} data-testid={rowQaId(`icd-chip-label-${idx}`, item.id)} data-qa={rowQaId(`icd-chip-label-${idx}`, item.id)} style={{ fontSize: 11, fontWeight: 600, color: style.color }}>{c.label}{c.inactive ? " (Inactive)" : ""}</span>
                    <span id={rowQaId(`icd-chip-type-${idx}`, item.id)} data-testid={rowQaId(`icd-chip-type-${idx}`, item.id)} data-qa={rowQaId(`icd-chip-type-${idx}`, item.id)} style={{ fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 4, background: "rgba(255,255,255,0.2)", color: style.color, opacity: 0.85, border: `1px solid ${style.border}` }}>{style.tag}</span>
                  </div>
                );
              })}
            </div>
            <div
              id={rowQaId("icd-chip-footer", item.id)}
              data-testid={rowQaId("icd-chip-footer", item.id)}
              data-qa={rowQaId("icd-chip-footer", item.id)}
              className="flex items-center justify-between border-t border-gray-100 pt-1"
            >
              <span
                id={rowQaId("icd-total-count", item.id)}
                data-testid={rowQaId("icd-total-count", item.id)}
                data-qa={rowQaId("icd-total-count", item.id)}
                className="text-xs font-bold text-gray-500"
              >
                {hasSearch && matchingValues.length > 0 ? `${matchingValues.length}/${allValues.length}` : allValues.length}
              </span>
              {remainingCount > 0 && (
                <button
                  id={rowQaId("btn-show-more-icd", item.id)}
                  data-testid={rowQaId("btn-show-more-icd", item.id)}
                  data-qa={rowQaId("btn-show-more-icd", item.id)}
                  type="button"
                  onClick={() => { setAllCodesItems(hasSearch && matchingValues.length > 0 ? matchingValues : allValues); setAllCodesTitle(`${item.name} – ICD Codes`); setAllCodesOpen(true); }}
                  className={`text-xs font-semibold ${hasSearch ? "text-yellow-600 hover:text-yellow-800" : "text-blue-600 hover:text-blue-800"}`}>
                  +{remainingCount} more →
                </button>
              )}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "is_active",
      enableSorting: false,
      header: () => <div {...qaAttrs("column-status-header")} className="w-full text-center normal-case font-normal">Status</div>,
      cell: ({ row }) => {
        const item = row.original;
        const isDeleted = (item as any).is_deleted;
        return (
          <div className="w-full flex justify-center">
            <button
              id={rowQaId("toggle-status", item.id)}
              data-testid={rowQaId("toggle-status", item.id)}
              data-qa={rowQaId("toggle-status", item.id)}
              data-qa-status={item.is_active ? "active" : "inactive"}
              data-qa-deleted={isDeleted ? "true" : "false"}
              type="button"
              onClick={() => !isDeleted && toggleActiveStatus(item)}
              disabled={!!isDeleted}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isDeleted ? "opacity-40 cursor-not-allowed bg-gray-200" : item.is_active ? "bg-green-500" : "bg-gray-300"}`}>
              <span
                id={rowQaId("toggle-status-knob", item.id)}
                data-testid={rowQaId("toggle-status-knob", item.id)}
                data-qa={rowQaId("toggle-status-knob", item.id)}
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${item.is_active && !isDeleted ? "translate-x-6" : "translate-x-1"}`}
              />
            </button>
          </div>
        );
      },
    },
    {
      id: "view",
      enableSorting: false,
      header: () => <div {...qaAttrs("column-view-header")} className="w-full text-center font-normal">View</div>,
      cell: ({ row }) => (
        <div className="w-full flex justify-center">
          <Button
            id={rowQaId("btn-view-details", row.original.id)}
            data-testid={rowQaId("btn-view-details", row.original.id)}
            data-qa={rowQaId("btn-view-details", row.original.id)}
            size="sm"
            variant="ghost"
            className="p-2 hover:bg-blue-50 text-blue-600 transition"
            onClick={() => handleOpenDialog(row.original)}
            title="View Details"
          >
            <Eye
              id={rowQaId("icon-view-details", row.original.id)}
              data-testid={rowQaId("icon-view-details", row.original.id)}
              data-qa={rowQaId("icon-view-details", row.original.id)}
              className="text-lg"
            />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div
      {...qaAttrs("page")}
      className="h5-container mx-auto px-1 pt-16 pb-4"
      data-page-name="custom-icd-groups"
    >
      <div {...qaAttrs("page-header")} className="flex justify-between items-center">
        <h5 {...qaAttrs("page-title")} className="mb-6 flex items-center gap-2 font-bold text-2xl mt-4 text-blue-900">
          <span {...qaAttrs("page-title-icon-wrapper")} className="icon-container flex items-center justify-center w-10 h-10 rounded-full bg-blue-900 text-white">
            <i {...qaAttrs("page-title-icon")} className="fa-solid fa-file-medical-alt"></i>
          </span>
          Custom ICD Groups
        </h5>
      </div>

      <div {...qaAttrs("data-table-wrapper")}>
      <DataTable2
        qaIdPrefix={qaId("data-table")}
        columns={columns}
        data={filteredData}
        searchColumns={[]}
        addButtonLabel="Add New Group"
        showAddButton={false}
        onEdit={handleEdit}
        pageIndex={currentPage}
        onPageChange={setCurrentPage}
        manualPagination={manualPagination}
        pageCount={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageSizeChange={(nextPageSize) => {
          setPageSize(nextPageSize);
          setCurrentPage(0);
        }}
        allowedPageSizes={allowedPageSizes}
        isLoading={isTableLoading}
        onDelete={(id) => {
          const item = items.result.find(i => i.id === id);
          if ((item as any)?.is_deleted) {
            setForceId(id); setForceAction("hardDelete"); setForceError(""); setForceDialogOpen(true);
          } else {
            removeItem(id);
          }
        }}
        onRestore={canManageDeleted ? restoreItem : undefined}
canHardDelete={canManageDeleted}
        isRowDeleted={(row: any) => row.is_deleted === true}
        onAdd={handleAddClick}
        onEditClick={handleEditClick}
        onDownload={handleDownload}
        toolbarExtras={
          <div {...qaAttrs("toolbar-extras")} style={{ width: "100%", position: "relative" }}>
<div {...qaAttrs("toolbar-row")} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "10px 160px 10px 16px", background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)", borderRadius: 12, border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.04)", marginBottom: 12, boxSizing: "border-box" as const }}>
              <button {...qaAttrs("btn-toolbar-add-new-group")} type="button" onClick={handleAddClick} style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 16px", fontSize: 13, fontWeight: 600, color: "#ffffff", background: "#1e3a8a", border: "none", borderRadius: 8, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0, boxShadow: "0 2px 6px rgba(30,58,138,0.25)" }}>
                <svg {...qaAttrs("btn-toolbar-add-new-group-icon")} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>
                Add New Group
              </button>
              <div {...qaAttrs("wrapper-toolbar-search")}>
              <UnifiedSearchBox
                value={filterSearch}
                onChange={(value) => { setFilterSearch(value); setCurrentPage(0); }}
                helpText="Group Name, ICD, Medical Group, or Custom Group."
              />
              </div>
              <div style={{ width: 1, height: 28, background: "#cbd5e1", flexShrink: 0 }} />
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                <span {...qaAttrs("status-filter-label")} style={{ fontSize: 11, fontWeight: 600, color: "#64748b", letterSpacing: "0.05em" }}>Status</span>
                <div {...qaAttrs("status-filter-wrapper")} style={{ display: "flex", alignItems: "center", padding: 3, background: "#e2e8f0", borderRadius: 9999 }}>
                  {[{ value: "", label: "All" }, { value: "true", label: "Active" }, { value: "false", label: "Inactive" }].map((option) => (
                    <button
                      key={option.value}
                      id={qaId(`btn-filter-status-${option.value || "all"}`)}
                      data-testid={qaId(`btn-filter-status-${option.value || "all"}`)}
                      data-qa={qaId(`btn-filter-status-${option.value || "all"}`)}
                      data-qa-selected={filterStatus === option.value ? "true" : "false"}
                      type="button"
                      onClick={() => { setFilterStatus(option.value); setCurrentPage(0); }}
                      style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 11px", fontSize: 12, fontWeight: 500, borderRadius: 9999, border: "none", cursor: "pointer", transition: "all 0.2s ease", whiteSpace: "nowrap", background: filterStatus === option.value ? option.value === "true" ? "#10b981" : option.value === "false" ? "#94a3b8" : "#ffffff" : "transparent", color: filterStatus === option.value ? option.value === "" ? "#334155" : "#ffffff" : "#64748b", boxShadow: filterStatus === option.value ? "0 2px 4px rgba(0,0,0,0.1)" : "none" }}>
                      {option.value === "true" && filterStatus === "true" && (<span style={{ width: 5, height: 5, borderRadius: "50%", background: "#ffffff" }} />)}
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <div {...qaAttrs("deleted-filter-wrapper")} style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                <ServerDeletedPills
                  value={deletedFilter}
                  onChange={(value) => { setDeletedFilter(value); setCurrentPage(0); }}
                />
              </div>
            </div>
            <div {...qaAttrs("file-upload-wrapper")} style={{ position: "absolute", top: 0, right: 16, height: 62, display: "flex", alignItems: "center" }}>
              <FileUpload fetchItems={fetchItems} />
            </div>
          </div>
        }
      />
      </div>

      {/* ── ICD List Dialog ── */}
      <Dialog open={allCodesOpen} onOpenChange={setAllCodesOpen}>
        <DialogContent {...qaAttrs("dialog-all-codes")} className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle {...qaAttrs("dialog-all-codes-title")} className="flex items-center gap-2">
              <i {...qaAttrs("dialog-all-codes-title-icon")} className="fa-solid fa-file-medical text-blue-600" />
              {allCodesTitle}
              <span className="ml-2 text-sm font-normal text-gray-400">({allCodesItems.length} codes)</span>
            </DialogTitle>
          </DialogHeader>
          <div {...qaAttrs("dialog-all-codes-body")} className="mt-3 overflow-y-auto max-h-[calc(80vh-120px)] space-y-4">
            {allCodesItems.filter((x) => x.type === "ICD").length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: "#011934", display: "inline-block" }} />
                  <span className="text-xs font-bold text-gray-600 tracking-wide">ICD Codes ({allCodesItems.filter((x) => x.type === "ICD").length})</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {allCodesItems.filter((x) => x.type === "ICD").map((c, i) => (<span key={i} id={qaId(`dialog-all-codes-icd-item-${i}`)} data-testid={qaId(`dialog-all-codes-icd-item-${i}`)} data-qa={qaId(`dialog-all-codes-icd-item-${i}`)} data-qa-label={c.label} style={{ padding: "4px 12px", background: "#011934", color: "white", borderRadius: 6, fontSize: 12, fontWeight: 600 }}>{c.label}</span>))}
                </div>
              </div>
            )}
            {allCodesItems.filter((x) => x.type === "Medical").length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: "#16a34a", display: "inline-block" }} />
                  <span className="text-xs font-bold text-green-700 tracking-wide">Medical ICD Groups ({allCodesItems.filter((x) => x.type === "Medical").length})</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {allCodesItems.filter((x) => x.type === "Medical").map((c, i) => (<span key={i} id={qaId(`dialog-all-codes-medical-item-${i}`)} data-testid={qaId(`dialog-all-codes-medical-item-${i}`)} data-qa={qaId(`dialog-all-codes-medical-item-${i}`)} data-qa-label={c.label} style={{ padding: "4px 12px", background: c.inactive ? "#e2e8f0" : "#f0fdf4", color: c.inactive ? "#64748b" : "#15803d", borderRadius: 6, fontSize: 12, fontWeight: 600, border: `1px solid ${c.inactive ? "#cbd5e1" : "#86efac"}` }}>{c.label}{c.inactive ? " (Inactive)" : ""}</span>))}
                </div>
              </div>
            )}
            {allCodesItems.filter((x) => x.type === "Custom").length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: "#9333ea", display: "inline-block" }} />
                  <span className="text-xs font-bold text-purple-700 tracking-wide">Custom ICD Groups ({allCodesItems.filter((x) => x.type === "Custom").length})</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {allCodesItems.filter((x) => x.type === "Custom").map((c, i) => (<span key={i} id={qaId(`dialog-all-codes-custom-item-${i}`)} data-testid={qaId(`dialog-all-codes-custom-item-${i}`)} data-qa={qaId(`dialog-all-codes-custom-item-${i}`)} data-qa-label={c.label} style={{ padding: "4px 12px", background: c.inactive ? "#e2e8f0" : "#faf5ff", color: c.inactive ? "#64748b" : "#7e22ce", borderRadius: 6, fontSize: 12, fontWeight: 600, border: `1px solid ${c.inactive ? "#cbd5e1" : "#d8b4fe"}` }}>{c.label}{c.inactive ? " (Inactive)" : ""}</span>))}
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end pt-3 border-t mt-3">
            <Button {...qaAttrs("btn-close-all-codes-dialog")} onClick={() => setAllCodesOpen(false)} className="bg-blue-700 hover:bg-blue-800">Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── View Details Dialog ── */}
      <Dialog open={Boolean(selectedItem)} onOpenChange={(open) => { if (!open) handleCloseDialog(); }}>
        <DialogContent {...qaAttrs("dialog-view-details")} className="max-w-[780px] max-h-[92vh] flex flex-col p-0 overflow-hidden bg-white border-none rounded-2xl" style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)" }}>
          {selectedItem && (
            <>
              <div {...qaAttrs("view-details-header")} style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)", padding: "24px 28px 20px", flexShrink: 0, position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
                <div style={{ position: "absolute", bottom: -30, left: -20, width: 100, height: 100, borderRadius: "50%", background: "rgba(255,255,255,0.03)" }} />
                <div style={{ display: "flex", alignItems: "flex-start", gap: 16, position: "relative", zIndex: 1 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 14, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, color: "#fff", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)" }}>
                    <i {...qaAttrs("view-details-header-icon")} className="fa-solid fa-file-medical-alt" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 2 }}>
                      <h2 {...qaAttrs("view-details-name")} style={{ fontSize: 20, fontWeight: 700, color: "#fff", margin: 0, letterSpacing: "-0.01em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{selectedItem.name}</h2>
                      <span {...qaAttrs("view-details-status-badge")} data-qa-status={selectedItem.is_active ? "active" : "inactive"} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, flexShrink: 0, background: selectedItem.is_active ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.15)", color: selectedItem.is_active ? "#86efac" : "#fca5a5", border: `1px solid ${selectedItem.is_active ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.2)"}` }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} />
                        {selectedItem.is_active ? "Active" : "Inactive"}
                      </span>
                      {(selectedItem as any).is_deleted && (
                        <span {...qaAttrs("view-details-deleted-badge")} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, flexShrink: 0, background: "rgba(239,68,68,0.15)", color: "#fca5a5", border: "1px solid rgba(239,68,68,0.2)" }}>
                          <i {...qaAttrs("view-details-deleted-icon")} className="fa-solid fa-trash" style={{ fontSize: 10 }} /> Deleted
                        </span>
                      )}
                    </div>
                    <p {...qaAttrs("view-details-group-id")} style={{ fontSize: 12, color: "rgba(147,197,253,0.8)", margin: 0, fontWeight: 400 }}>Group ID: <span style={{ fontWeight: 600, color: "#93c5fd" }}>#{selectedItem.id}</span></p>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginTop: 18, position: "relative", zIndex: 1 }}>
                  {[
                    { icon: "fa-file-medical", label: "ICD Codes", value: `${(selectedItem.icd || []).length} codes`, accent: "#60a5fa" },
                    { icon: "fa-layer-group", label: "Medical Groups", value: `${(selectedItem.medical_groups || []).length}`, accent: "#a78bfa" },
                    { icon: "fa-cubes", label: "Custom Groups", value: `${(selectedItem.custom_groups || []).length}`, accent: "#34d399" },
                    { icon: "fa-align-left", label: "Description", value: selectedItem.description ? "Available" : "None", accent: "#f59e0b" },
                  ].map((card, i) => (
                    <div key={i} style={{ background: "rgba(255,255,255,0.07)", backdropFilter: "blur(6px)", borderRadius: 12, padding: "12px 14px", border: "1px solid rgba(255,255,255,0.08)" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                        <i className={`fa-solid ${card.icon}`} style={{ fontSize: 11, color: card.accent, opacity: 0.9 }} />
                        <span style={{ fontSize: 10, color: "rgba(147,197,253,0.7)", fontWeight: 500, letterSpacing: "0.03em" }}>{card.label}</span>
                      </div>
                      <span style={{ fontSize: 15, fontWeight: 700, color: card.accent }}>{card.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div {...qaAttrs("view-details-body")} style={{ overflowY: "auto", flex: 1, background: "#f8fafc", padding: "6px 0 16px" }}>
                {selectedItem.description && (
                  <>
                    <div style={{ padding: "20px 28px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                        <div style={{ width: 28, height: 28, borderRadius: 8, background: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #fde68a" }}>
                          <i className="fa-solid fa-align-left" style={{ fontSize: 12, color: "#d97706" }} />
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Description</span>
                      </div>
                      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                        <p {...qaAttrs("view-details-description")} style={{ margin: 0, fontSize: 14, color: "#334155", lineHeight: 1.6 }}>{selectedItem.description}</p>
                      </div>
                    </div>
                    <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />
                  </>
                )}

                <div style={{ padding: "20px 28px 16px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #dbeafe" }}>
                      <i className="fa-solid fa-file-medical" style={{ fontSize: 12, color: "#3b82f6" }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>ICD Codes</span>
                    <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 500 }}>{(selectedItem.icd || []).length} items</span>
                  </div>
                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                    <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 6 }}>
                      {(selectedItem.icd || []).length > 0
                        ? (selectedItem.icd || []).map((code: string, i: number) => (<span key={i} style={{ display: "inline-flex", alignItems: "center", padding: "3px 10px", borderRadius: 9999, fontSize: 11, fontWeight: 500, background: "#eff6ff", color: "#1d4ed8", border: "0.5px solid #bfdbfe" }}>{code}</span>))
                        : <span style={{ color: "#94a3b8", fontSize: 12, fontStyle: "italic" }}>No ICD codes</span>}
                    </div>
                  </div>
                </div>

                {((selectedItem.medical_groups || []).length > 0 || (selectedItem.custom_groups || []).length > 0) && (
                  <>
                    <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />
                    <div style={{ padding: "20px 28px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                        <div style={{ width: 28, height: 28, borderRadius: 8, background: "#faf5ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #e9d5ff" }}>
                          <i className="fa-solid fa-layer-group" style={{ fontSize: 12, color: "#a855f7" }} />
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Groups</span>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                        {[
                          { label: "Medical ICD Groups", color: "#22c55e", items: (selectedItem.medical_groups || []).map((g: any) => ({ label: String(g?.name ?? g?.label ?? g), inactive: isLinkedGroupInactive(g) })), tagStyle: { background: "#f0fdf4", color: "#15803d", border: "0.5px solid #bbf7d0" } },
                          { label: "Custom ICD Groups", color: "#a855f7", items: (selectedItem.custom_groups || []).map((g: any) => ({ label: String(g?.name ?? g?.label ?? g), inactive: isLinkedGroupInactive(g) })), tagStyle: { background: "#faf5ff", color: "#7e22ce", border: "0.5px solid #e9d5ff" } },
                        ].map((section) => (
                          <div key={section.label} style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                              <span style={{ width: 6, height: 6, borderRadius: 2, background: section.color, flexShrink: 0 }} />
                              <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b", letterSpacing: "0.02em" }}>{section.label}</span>
                            </div>
                            <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 5 }}>
                              {section.items.length > 0
                                ? section.items.map((item: { label: string; inactive: boolean }, i: number) => (<span key={i} id={qaId(`view-details-group-item-${qaSafe(section.label)}-${i}`)} data-testid={qaId(`view-details-group-item-${qaSafe(section.label)}-${i}`)} data-qa={qaId(`view-details-group-item-${qaSafe(section.label)}-${i}`)} data-qa-label={item.label} style={{ display: "inline-flex", alignItems: "center", padding: "3px 10px", borderRadius: 9999, fontSize: 11, fontWeight: 500, ...section.tagStyle, ...(item.inactive ? { background: "#e2e8f0", color: "#64748b", border: "0.5px solid #cbd5e1" } : {}) }}>{item.label}{item.inactive ? " (Inactive)" : ""}</span>))
                                : <span style={{ color: "#94a3b8", fontSize: 12, fontStyle: "italic" }}>None</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />

                <div style={{ padding: "20px 28px 8px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
                      <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: 12, color: "#16a34a" }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Audit Trail</span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", letterSpacing: "0.02em" }}>Created By</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #3b82f6, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                          {String((selectedItem as any).created_by ?? "?")[0].toUpperCase()}
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{resolveUserName((selectedItem as any).created_by)}</span>
                      </div>
                    </div>
                    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", letterSpacing: "0.02em" }}>Created On</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                        <i className="fa-regular fa-calendar" style={{ fontSize: 14, color: "#3b82f6" }} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{formatDate((selectedItem as any).createdat)}</span>
                      </div>
                    </div>
                    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", letterSpacing: "0.02em" }}>Last Updated</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                        <i className="fa-regular fa-clock" style={{ fontSize: 14, color: "#10b981" }} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{formatDate((selectedItem as any).updatedat)}</span>
                      </div>
                    </div>
                  </div>
          {Array.isArray((selectedItem as any).updated_by) && (selectedItem as any).updated_by.length > 0 && (
  <div style={{ gridColumn: "1 / -1", background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
      <i className="fa-solid fa-pen-to-square" style={{ fontSize: 11, color: "#10b981" }} />
      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.02em" }}>Update History</span>
      <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#f0fdf4", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0", marginLeft: "auto" }}>
        {(selectedItem as any).updated_by.length} update{(selectedItem as any).updated_by.length !== 1 ? "s" : ""}
      </span>
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {((selectedItem as any).updated_by as { id?: number; user_id?: number; at?: string; date?: string; [key: string]: any }[]).slice().reverse().slice(0, showAllUpdates ? undefined : 4).map((entry, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: i === 0 ? "#f0fdf4" : "#f8fafc", border: `1px solid ${i === 0 ? "#bbf7d0" : "#e2e8f0"}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: i === 0 ? "linear-gradient(135deg, #10b981, #059669)" : "linear-gradient(135deg, #94a3b8, #64748b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
              {String(resolveUserName(entry.id ?? entry.user_id) ?? "?")[0].toUpperCase()}
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{resolveUserName(entry.id ?? entry.user_id)}</span>
            {i === 0 && (<span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#dcfce7", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0" }}>Latest</span>)}
          </div>
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>{formatDate(entry.date || entry.at || (entry as any).updatedat || (entry as any).timestamp || (i === 0 ? (selectedItem as any).updatedat : undefined))}</span>
        </div>
      ))}
      {(selectedItem as any).updated_by.length > 4 && (
        <div style={{ textAlign: "center", padding: "6px 0" }}>
          <button
            {...qaAttrs("btn-toggle-update-history")}
            type="button"
            onClick={() => setShowAllUpdates(v => !v)}
            style={{ fontSize: 11, color: "#3b82f6", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}
          >
            {showAllUpdates ? "Show less ↑" : `+${(selectedItem as any).updated_by.length - 4} more updates ↓`}
          </button>
        </div>
      )}
    </div>
  </div>
)}
                </div>
              </div>

              <div style={{ padding: "14px 28px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, flexShrink: 0 }}>
                <Button {...qaAttrs("btn-close-view-details")} onClick={handleCloseDialog} style={{ background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "#fff", fontWeight: 600, padding: "8px 28px", borderRadius: 10, boxShadow: "0 2px 8px rgba(37,99,235,0.3)" }}>Close Details</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Add / Edit Dialog ── */}
 {/* ── Add / Edit Dialog ── */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent {...qaAttrs("dialog-add-edit")} className="max-w-3xl h-[85vh] flex flex-col overflow-hidden p-0">
          <div {...qaAttrs("dialog-add-edit-header")} className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
            <DialogHeader>
              <DialogTitle {...qaAttrs("dialog-add-edit-title")} className="text-lg font-semibold text-gray-900">
                {editIndex === null ? "Add Custom ICD Group" : "Edit Custom ICD Group "}
              </DialogTitle>
              </DialogHeader>
          </div>

          <div {...qaAttrs("dialog-add-edit-body")} className="flex-1 overflow-y-auto px-6 py-5 space-y-0">
            {/* Section 1 */}
   {/* Section 1 */}
            <SectionCard num={1} title="Basic Information" subtitle="Name, description, and status of the group" automationId={qaId("section-basic-information")}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label {...qaAttrs("label-name")} htmlFor={qaId("input-name")}>Name <span className="text-red-500 ml-0.5">*</span></Label>
                  <Input {...qaAttrs("input-name")} value={Name} onChange={(e) => setName(e.target.value)} placeholder="Must be at least 3 characters" className="mt-1" />
                </div>
                <div>
                  <Label {...qaAttrs("label-description")} htmlFor={qaId("input-description")}>Description</Label>
                  <Input {...qaAttrs("input-description")} value={Description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional description" className="mt-1" />
                </div>
                <div className="col-span-2 max-w-xs">
                  <StatusToggle qaIdPrefix={qaId("status-toggle-group")} label="Group Status" dotColor="#2563eb" value={isActive} onChange={setIsActive} />
                </div>
              </div>
            </SectionCard>

            {/* Section 2 */}
            <SectionCard num={2} title="ICD configuration" subtitle="At least one ICD code is required" automationId={qaId("section-icd-configuration")}>
              <div className="space-y-4">
                <div>
                  <Label {...qaAttrs("label-list-of-icd")} className="mb-1.5 block">List of ICD <span className="text-red-500 ml-0.5">*</span></Label>
                  <div {...qaAttrs("wrapper-list-of-icd")}>
                    <Listofmedicalcode onChange={handlemedicalcodeChange} description={IcdDescription} defaultValue={medicalcode} clear={clearmedicalcode} />
                  </div>
                </div>
                <div>
                  <Label {...qaAttrs("label-medical-icd-groups")} className="mb-1.5 block">Medical ICD Groups</Label>
                  <div {...qaAttrs("wrapper-medical-icd-groups")}>
                    <ListofMedicalGroup onChange={handlemedicalicdChange} defaultValue={medicalIcdGroups} clear={clearmedicalIcdGroups} />
                  </div>
                </div>
                <div>
                  <Label {...qaAttrs("label-custom-icd-groups")} className="mb-1.5 block">Custom ICD Groups</Label>
                  <div {...qaAttrs("wrapper-custom-icd-groups")}>
                    <ListofCustom onChange={handlecustomicdChange} defaultValue={CustomIcdGroups} clear={clearcustomIcdGroups} />
                  </div>
                </div>
              </div>
            </SectionCard>

            {/* Section 3 */}
         
          </div>

          <div {...qaAttrs("dialog-add-edit-footer")} className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
            <Button {...qaAttrs("btn-cancel-add-edit")} variant="outline" onClick={() => { setEditIndex(null); clear(); setIsDialogOpen(false); }}>Cancel</Button>
            <Button
              id={qaId(editIndex === null ? "btn-add-group-submit" : "btn-update-group-submit")}
              data-testid={qaId("btn-save-group")}
              data-qa={qaId(editIndex === null ? "btn-add-group-submit" : "btn-update-group-submit")}
              data-qa-action={editIndex === null ? "add-group" : "update-group"}
              onClick={() => { editIndex === null ? addItem() : updateItem(editIndex); }}
              className="bg-blue-700 hover:bg-blue-800 text-white"
            >
              {editIndex === null ? "Add Group " : "Update Group"} 
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Force / Hard Delete Dialog ── */}
      {forceDialogOpen && (
        <div {...qaAttrs("dialog-force-delete-overlay")} style={{ position: "fixed", inset: 0, background: "#00000088", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" }}>
          <div {...qaAttrs("dialog-force-delete")} data-qa-force-action={forceAction ?? ""} data-qa-force-id={forceId ?? ""} style={{ background: "#fff", borderRadius: 16, padding: 32, width: 440, boxShadow: "0 20px 50px rgba(0,0,0,0.3)", border: "1px solid #fecaca" }}>
            <div {...qaAttrs("force-delete-content")} style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 12 }}>
              <div {...qaAttrs("force-delete-alert-icon-wrapper")} style={{ width: 60, height: 60, borderRadius: "50%", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i {...qaAttrs("force-delete-alert-icon")} className="fa-solid fa-triangle-exclamation" style={{ fontSize: 28, color: "#dc2626" }}></i>
              </div>
{forceAction === "hardDelete" && (
  <>
    <h3 {...qaAttrs("hard-delete-alert-title")} style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>
      Permanent Delete
    </h3>
    <p {...qaAttrs("hard-delete-alert-message")} style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
      This will <strong style={{ color: "#dc2626" }}>permanently delete</strong> the record.<br />
      This action <strong>cannot be undone</strong>.
    </p>
    <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
      <button
        {...qaAttrs("btn-cancel-hard-delete")}
        data-qa-action="cancel-hard-delete"
        onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }}
        style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
        Cancel
      </button>
      <button
        {...qaAttrs("btn-confirm-hard-force-delete")}
        data-qa-action="confirm-hard-force-delete"
        onClick={() => forceId !== null && permanentDeleteItem(forceId, true)}
        style={{ flex: 1, background: "#7f1d1d", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
        <i {...qaAttrs("icon-confirm-hard-force-delete")} className="fa-solid fa-bolt mr-2"></i>Force Delete Anyway
      </button>
    </div>
  </>
)}
          {forceAction === "softDelete" && (
            <>
              <h3 {...qaAttrs("soft-delete-alert-title")} style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>
                Record Is In Use
              </h3>
              <div {...qaAttrs("soft-delete-error-container")} style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <i {...qaAttrs("soft-delete-error-icon")} className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                  <p {...qaAttrs("soft-delete-error-message")} style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
                </div>
              </div>
              <p {...qaAttrs("soft-delete-confirmation-message")} style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
                Do you want to <strong style={{ color: "#dc2626" }}>force delete</strong> anyway?
              </p>
              <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
                <button
                  {...qaAttrs("btn-cancel-soft-delete")}
                  data-qa-action="cancel-soft-delete"
                  onClick={() => { setForceDialogOpen(false); setForceId(null); setForceTargetItem(null); setForceError(""); setForceAction(null); }}
                  style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                  Cancel
                </button>
                <button
                  {...qaAttrs("btn-confirm-soft-force-delete")}
                  data-qa-action="confirm-soft-force-delete"
                  onClick={() => {
                    if (!forceId) return;
                    forceSoftDelete(forceId);
                  }}
                  style={{ flex: 1, background: "#dc2626", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                  <i {...qaAttrs("icon-confirm-soft-force-delete")} className="fa-solid fa-bolt" style={{ marginRight: 8 }}></i>
                  Force Delete
                </button>
              </div>
            </>
          )}

          {forceAction === "status" && (
            <>
              <h3 {...qaAttrs("status-force-alert-title")} style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>
                ⚠️ Warning
              </h3>
              <div {...qaAttrs("status-force-error-container")} style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <i {...qaAttrs("status-force-error-icon")} className="fa-solid fa-triangle-exclamation" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                  <p {...qaAttrs("status-force-error-message")} style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6, fontWeight: 600 }}>
                    ⚠️ “Warning: This group is currently used in predefined rule relations. Deactivating it will disable all dependent relations.”
                  </p>
                </div>
              </div>
              <p {...qaAttrs("status-force-confirmation-message")} style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
                Do you want to <strong style={{ color: "#dc2626" }}>deactivate</strong> anyway?
              </p>
              <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
                <button
                  {...qaAttrs("btn-cancel-status-force-update")}
                  data-qa-action="cancel-status-force-update"
                  onClick={() => { setForceDialogOpen(false); setForceId(null); setForceTargetItem(null); setForceError(""); setForceAction(null); }}
                  style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                  Cancel
                </button>
                <button
                  {...qaAttrs("btn-confirm-status-force-update")}
                  data-qa-action="confirm-status-force-update"
                  onClick={() => {
                    if (!forceId) return;
                    if (forceTargetItem) toggleActiveStatus(forceTargetItem, true);
                  }}
                  style={{ flex: 1, background: "#dc2626", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                  <i {...qaAttrs("icon-confirm-status-force-update")} className="fa-solid fa-bolt" style={{ marginRight: 8 }}></i>
                  Deactivate Anyway
                </button>
              </div>
            </>
          )}
            </div>
          </div>
        </div>
      )}

      <div {...qaAttrs("toaster-wrapper")}>
        <Toaster />
      </div>
    </div>
  );
};

export default CustomIcdGroups;
